import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getQuizCards, Card } from '../api';

interface QuizQuestion {
  card: Card;
  options: string[];
  correct: number;
}

function buildQuiz(cards: Card[]): QuizQuestion[] {
  return cards.map(card => {
    const others = cards.filter(c => c.id !== card.id);
    const wrongs = others.sort(() => Math.random() - 0.5).slice(0, 3).map(c => c.back);
    const allOpts = [...wrongs, card.back].sort(() => Math.random() - 0.5);
    return {
      card,
      options: allOpts,
      correct: allOpts.indexOf(card.back),
    };
  });
}

export default function QuizPage() {
  const { id } = useParams<{ id: string }>();
  const deckId = Number(id);

  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wrongList, setWrongList] = useState<Card[]>([]);

  const loadQuiz = useCallback(async () => {
    setLoading(true);
    const cards = await getQuizCards(deckId, 10);
    if (cards.length < 4) {
      setQuestions([]);
    } else {
      setQuestions(buildQuiz(cards));
    }
    setQIndex(0);
    setSelected(null);
    setScore(0);
    setDone(false);
    setWrongList([]);
    setLoading(false);
  }, [deckId]);

  useEffect(() => { loadQuiz(); }, [loadQuiz]);

  const current = questions[qIndex];
  const isJa = current?.card?.front?.match(/[\u3040-\u30ff\u4e00-\u9faf]/);

  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    const correct = idx === current.correct;
    if (correct) setScore(s => s + 1);
    else setWrongList(w => [...w, current.card]);
    setTimeout(() => {
      if (qIndex + 1 >= questions.length) {
        setDone(true);
      } else {
        setQIndex(i => i + 1);
        setSelected(null);
      }
    }, 900);
  };

  const getEmoji = () => {
    const pct = score / questions.length;
    if (pct >= 0.9) return '🏆';
    if (pct >= 0.7) return '🎉';
    if (pct >= 0.5) return '💪';
    return '📚';
  };

  if (loading) return <div className="loading"><div className="spinner" />Đang chuẩn bị quiz...</div>;

  if (questions.length === 0) return (
    <div className="quiz-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
      <div className="empty-state">
        <div className="empty-icon">❓</div>
        <p>Cần ít nhất 4 thẻ để làm quiz.</p>
        <Link to={`/deck/${deckId}`} className="btn btn-primary">+ Thêm thẻ</Link>
      </div>
    </div>
  );

  if (done) return (
    <div className="quiz-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
      <div className="quiz-result">
        <div className="result-emoji">{getEmoji()}</div>
        <div className="result-score">{score}/{questions.length}</div>
        <div className="result-label">câu đúng · {Math.round((score / questions.length) * 100)}%</div>

        {wrongList.length > 0 && (
          <div style={{ textAlign: 'left', background: '#fee2e2', borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Cần ôn lại ({wrongList.length} từ):
            </div>
            {wrongList.map(c => (
              <div key={c.id} style={{ fontSize: '0.875rem', padding: '3px 0', color: '#7f1d1d' }}>
                <strong>{c.front}</strong> → {c.back}
              </div>
            ))}
          </div>
        )}

        <div className="result-actions">
          <button className="btn btn-accent btn-lg" onClick={loadQuiz}>🔄 Quiz lại</button>
          <Link to={`/deck/${deckId}`} className="btn btn-ghost btn-lg">← Về bộ từ</Link>
        </div>
      </div>
    </div>
  );

  const progress = ((qIndex) / questions.length) * 100;

  return (
    <div className="quiz-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>

      <div className="flashcard-progress">
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--accent)' }} /></div>
        <div className="progress-text">Câu {qIndex + 1}/{questions.length} · Điểm: {score}</div>
      </div>

      <div className="quiz-question">
        <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
          Nghĩa của từ này là gì?
        </div>
        <div className="quiz-word">{current.card.front}</div>
        {isJa && current.card.reading && (
          <div className="quiz-reading">{current.card.reading}</div>
        )}
      </div>

      <div className="quiz-options">
        {current.options.map((opt, idx) => {
          let cls = 'quiz-option';
          if (selected !== null) {
            if (idx === current.correct) cls += ' correct';
            else if (idx === selected) cls += ' wrong';
          }
          return (
            <button key={idx} className={cls} onClick={() => handleSelect(idx)} disabled={selected !== null}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
